import { Static, Type } from '@sinclair/typebox';

// =====================================================================
// Expéditeur de campagnes e-mail sur le domaine du pro (Mailjet) — contrats partagés.
//
// Un pro équipé envoie ses campagnes depuis `contact@mon-salon.fr` au lieu de
// `slug@<campaign_sender_domain>`. Le domaine doit être un `pro_website_domain` possédé, dont nous
// gérons la zone OVH : nous y posons nous-mêmes les records dictés par Mailjet (DKIM, jeton de
// propriété, include SPF). Cf. docs/to_implement/campaign-sender-domains-plan.md (lot B).
//
// ⚠️ Miroir de l'enum Prisma `CampaignSenderDomainStatus` (packages/backend/prisma-pg/schema.prisma)
// — garder l'ordre et les valeurs synchro.
// =====================================================================

/** État d'un domaine du pro déclaré comme expéditeur sur le compte Mailjet des campagnes. */
export enum CampaignSenderDomainStatus {
  /** Créé, sender Mailjet pas encore demandé. */
  PENDING = 'PENDING',
  /** Sender créé, records posés dans la zone, en attente de propagation / validation. */
  DNS_PENDING = 'DNS_PENDING',
  /** Propriété + SPF + DKIM validés par Mailjet : utilisable. */
  ACTIVE = 'ACTIVE',
  /** Échec (plafond Mailjet, DNS KO au-delà du délai…) : voir `last_error`. */
  ERROR = 'ERROR',
  /** Retiré par le pro ou l'admin, sender supprimé chez Mailjet. */
  DISABLED = 'DISABLED',
}

/**
 * Partie locale de l'adresse d'expédition (avant le @). Minuscules, chiffres, `.`, `_`, `-`, ne
 * commence/finit pas par un séparateur.
 */
export const CAMPAIGN_SENDER_LOCAL_PART_PATTERN = '^[a-z0-9]([a-z0-9._-]*[a-z0-9])?$';
export const CAMPAIGN_SENDER_LOCAL_PART_MAX_LENGTH = 64;

/** Un seul domaine expéditeur par pro (chaque domaine consomme un expéditeur du plafond Mailjet). */
export const CAMPAIGN_SENDER_DOMAIN_MAX_PER_PRO = 1;

/**
 * Clés d'erreur stockées en tête de `last_error` (`<clé>: <détail Mailjet / OVH>`) : une seule
 * définition pour le processor qui les écrit, l'admin qui les affiche et le pro qui les lit.
 */
export enum CampaignSenderDomainErrorKey {
  /** Plafond d'expéditeurs du compte Mailjet atteint : rien n'a été posé, ticket Mailjet côté ops. */
  SENDER_LIMIT_REACHED = 'SENDER_LIMIT_REACHED',
  /** Les records n'ont pas pu être écrits dans la zone OVH. */
  DNS_WRITE_FAILED = 'DNS_WRITE_FAILED',
  /** Mailjet n'a pas validé SPF / DKIM / propriété dans le délai (≈ 48 h). */
  VALIDATION_TIMEOUT = 'VALIDATION_TIMEOUT',
  /** Domaine actif retombé en échec à plusieurs contrôles consécutifs (records retirés, clé tournée…). */
  RECHECK_FAILED = 'RECHECK_FAILED',
}

/**
 * Préfixe des TXT que Mailjet dicte pour un domaine expéditeur (`mailjet._domainkey`,
 * `mailjet._<jeton de propriété>`) : verrouillés dans le DNS self-service tant que le domaine est
 * configuré. Partagé entre le classifieur de zone et le processor qui les pose.
 */
export const CAMPAIGN_SENDER_TXT_SUBDOMAIN_PATTERN = /^mailjet\._/i;
export const isCampaignSenderTxtSubdomain = (subDomain: string): boolean =>
  CAMPAIGN_SENDER_TXT_SUBDOMAIN_PATTERN.test(subDomain);

// ── Requêtes ──

/** POST /app/campaign-sender-domain — déclarer un domaine possédé comme expéditeur. */
export const CreateCampaignSenderDomainRequestSchema = Type.Object({
  /** Domaine du pro (`pro_website_domain`), actif et dont la zone DNS est gérée par nous. */
  pro_website_domain_id: Type.Integer({ minimum: 1 }),
});
export type CreateCampaignSenderDomainRequest = Static<typeof CreateCampaignSenderDomainRequestSchema>;

/**
 * PUT /app/campaign-sender-domain/sender-email — choisir l'adresse d'expédition.
 * `local_part` null = revenir à l'adresse Propelsia par défaut.
 */
export const SetCampaignSenderEmailRequestSchema = Type.Object({
  local_part: Type.Union([
    Type.String({
      minLength: 1,
      maxLength: CAMPAIGN_SENDER_LOCAL_PART_MAX_LENGTH,
      pattern: CAMPAIGN_SENDER_LOCAL_PART_PATTERN,
    }),
    Type.Null(),
  ]),
});
export type SetCampaignSenderEmailRequest = Static<typeof SetCampaignSenderEmailRequestSchema>;

// ── Réponses ──

/** Records DNS dictés par Mailjet, tels que posés dans la zone (affichage et diagnostic). */
export const CampaignSenderDomainRecordsSchema = Type.Object({
  dkim_record_name: Type.Optional(Type.String()),
  dkim_record_value: Type.Optional(Type.String()),
  spf_include: Type.Optional(Type.String()),
  ownership_record_name: Type.Optional(Type.String()),
  ownership_token: Type.Optional(Type.String()),
});
export type CampaignSenderDomainRecords = Static<typeof CampaignSenderDomainRecordsSchema>;

/** Le domaine expéditeur du pro, avec son état de vérification. */
export const CampaignSenderDomainSchema = Type.Object({
  pro_campaign_sender_domain_id: Type.Number(),
  pro_website_domain_id: Type.Number(),
  domain: Type.String(),
  status: Type.Enum(CampaignSenderDomainStatus),
  /** Résultat Mailjet de la dernière vérification (`OK`, `Not found`, `Error`), vide avant la 1re. */
  dkim_status: Type.Optional(Type.String()),
  spf_status: Type.Optional(Type.String()),
  /** Clé d'erreur traduisible (`CAMPAIGN_SENDER_DOMAIN_ERROR_*`) ou message brut Mailjet. */
  last_error: Type.Optional(Type.String()),
  records: CampaignSenderDomainRecordsSchema,
  last_check_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'last_check_at', with_time: true } }),
  ),
  verified_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'verified_at', with_time: true } }),
  ),
  created_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'created_at', with_time: true } }),
  ),
});
export type CampaignSenderDomain = Static<typeof CampaignSenderDomainSchema>;

/** Un domaine du pro sur lequel l'expéditeur peut être configuré. */
export const CampaignSenderEligibleDomainSchema = Type.Object({
  pro_website_domain_id: Type.Number(),
  domain: Type.String(),
});
export type CampaignSenderEligibleDomain = Static<typeof CampaignSenderEligibleDomainSchema>;

/**
 * GET /app/campaign-sender-domain — réglage complet de l'expéditeur du pro : domaine configuré
 * (s'il y en a un), adresse choisie, adresse effectivement utilisée à l'envoi et domaines éligibles.
 */
export const CampaignSenderSettingsResponseSchema = Type.Object({
  /** Vrai si l'option marque blanche du pro ouvre la configuration (sinon : upsell). */
  can_configure: Type.Boolean(),
  sender_domain: Type.Optional(CampaignSenderDomainSchema),
  /** Adresse choisie par le pro sur son domaine (`contact@mon-salon.fr`), vide = adresse Propelsia. */
  campaign_sender_email: Type.Optional(Type.String()),
  /** Adresse Propelsia par défaut : `slug@<campaign_sender_domain>`. */
  default_sender_email: Type.String(),
  /**
   * Adresse qui partira au prochain envoi : celle du pro dès qu'il en a choisi une (domaine actif, ou
   * en cours d'activation : l'envoi attend, cf. `sender_pending`), sinon l'adresse Propelsia (aucune
   * adresse choisie, ou domaine en échec : repli).
   */
  effective_sender_email: Type.String(),
  /** Vrai quand l'adresse choisie attend l'activation de son domaine : les envois e-mail sont différés jusque-là. */
  sender_pending: Type.Boolean(),
  eligible_domains: Type.Array(CampaignSenderEligibleDomainSchema),
});
export type CampaignSenderSettingsResponse = Static<typeof CampaignSenderSettingsResponseSchema>;

/** GET /app/admin/professionals/:id/campaign-sender — bloc « Expéditeur campagnes » de la fiche pro. */
export const AdminCampaignSenderDomainResponseSchema = Type.Object({
  sender_domain: Type.Optional(CampaignSenderDomainSchema),
  campaign_sender_email: Type.Optional(Type.String()),
  default_sender_email: Type.String(),
});
export type AdminCampaignSenderDomainResponse = Static<typeof AdminCampaignSenderDomainResponseSchema>;
